import { HttpException } from "http-repository-cache";
import ScreeningRepository from "@/core/customers/repositories/ScreeningRepository";
import { ApiResponse } from "../../../api/types/ApiResponse";
import { ApiResponseHandler } from "../../../http/services/handleApiResponse/handleApiResponse";
import { HttpClient } from "../../../http/HttpClient";
import {
  screeningCardUrl,
  screeningPassportUrl,
  screeningStartUrl,
  screeningResultUrl,
} from "../../../api/screeningEndpoints/screeningEndpoints";
import ScreeningDataMapper from "@/core/customers/mappers/ScreeningDataMapper";

import {
  ApiScreeningResultResponse,
  ScreeningRequest,
  ScreeningResponse,
  ScreeningRuleCheck,
} from "@/core/customers/entities/Screening";
import handleRequestError from "../../../http/services/handleRequestError/handleRequestError";
import { executeRequest } from "../../../http/services/executeRequest/executeRequest";
import {
  RepositoryResult,
  RepositoryResultFactory,
} from "@/core/shared/types/RepositoryResult";
import ScreeningResultMapper from "@/core/customers/mappers/ScreeningResultMapper";
import {
  ApiScreeningRequest,
  ApiScreeningResponse,
} from "@/core/customers/entities/ApiDocumentUpload";

export default class ScreeningRepositoryBackend implements ScreeningRepository {
  constructor(private readonly httpClient: HttpClient) {}

  async runScreening(
    request: ScreeningRequest
  ): Promise<RepositoryResult<ScreeningResponse>> {
    // Determine the correct endpoint based on document type
    const url =
      request.documentType === "CNI"
        ? screeningCardUrl(request.businessKey)
        : screeningPassportUrl(request.businessKey);

    // Make the API call (note: body is optional/empty for this endpoint)
    return executeRequest(
      () =>
        this.httpClient.post<
          ApiResponse<ApiScreeningResponse>,
          ApiScreeningRequest | undefined
        >({
          url: url,
          withAuth: true,
        }),
      (data) => ScreeningDataMapper.fromApiResponse(data),
    );
  }

  async initiateScreening(
    businessKey: string
  ): Promise<RepositoryResult<boolean>> {
    try {
      const url = screeningStartUrl(businessKey);

      const response = await this.httpClient.get<ApiResponse<null>>({
        url,
        withAuth: true,
      });

      const result = ApiResponseHandler.handleResponse(response);

      if (result.success) {
        return RepositoryResultFactory.success(true);
      }

      return RepositoryResultFactory.error(
        ApiResponseHandler.createExceptionFromResponse(response)
      );
    } catch (error) {
      return RepositoryResultFactory.error(
        handleRequestError(error as HttpException<unknown, unknown>)
      );
    }
  }

  async getScreeningResult(
    businessKey: string
  ): Promise<RepositoryResult<ScreeningRuleCheck[]>> {
    const url = screeningResultUrl(businessKey);
    return executeRequest(
      () =>
        this.httpClient.get<ApiResponse<ApiScreeningResultResponse>>({
          url,
          withAuth: true,
        }),
      (data) => ScreeningResultMapper.fromApi(data),
    );
  }
}
